import { useState, useCallback, useEffect, useRef } from 'react';
import { SUPABASE_URL, SUPABASE_ANON } from '../data/constants';

// ── Constantes ────────────────────────────────────────────────────────────────
const isDev = process.env.NODE_ENV === 'development';
const log  = (...args) => { if (isDev) console.log(...args); };
const warn = (...args) => { if (isDev) console.warn(...args); };

const isValidKey = (k) => typeof k === 'string' && k.startsWith('eyJ');
const USE_SUPA   = !!(SUPABASE_URL && SUPABASE_ANON && isValidKey(SUPABASE_ANON));

// ── Snapshot localStorage ─────────────────────────────────────────────────────
// Le localStorage sert UNIQUEMENT de snapshot de secours (Supabase KO).
// Il est écrit après chaque UPSERT Supabase réussi → toujours à jour.
// Au démarrage, Supabase est toujours la source primaire.
// Les clés sensibles (RGPD) ne sont jamais écrites en localStorage.
const SENSITIVE_KEYS = new Set([
  'cmob_staff', 'cmob_medecine', 'cmob_profils', 'cmob_conges',
  'cmob_remunerations', 'cmob_contrats_gen', 'cmob_absences_np',
  'cmob_entretiens', 'cmob_notes_rh', 'cmob_variables_paie', 'cmob_rup',
  'cmob_docs', 'cmob_onboarding', // données personnelles — jamais en localStorage
]);

function snapshotSave(key, payload) {
  if (SENSITIVE_KEYS.has(key)) return;
  try { localStorage.setItem('snap_' + key, JSON.stringify({ ts: Date.now(), payload })); } catch {}
}

function snapshotLoad(key) {
  try {
    const raw = localStorage.getItem('snap_' + key);
    if (!raw) return undefined;
    const { ts, payload } = JSON.parse(raw);
    const ageMin = Math.round((Date.now() - ts) / 60000);
    warn(`[useDB] Snapshot "${key}" utilisé (${ageMin} min)`);
    return payload;
  } catch { return undefined; }
}

// ── État de connectivité global ──────────────────────────────────────────────
let _isOnline = true;
const _onlineListeners = new Set();

function notifyOnlineStatus(status) {
  _isOnline = status;
  _onlineListeners.forEach(cb => cb(status));
}

// Ping Supabase toutes les 10s pour vérifier la connectivité
let _pingInterval = null;

async function pingSupabase() {
  if (!USE_SUPA || !_accessToken) return;
  try {
    const r = await fetch(`${SUPABASE_URL}/rest/v1/sirh_data?select=count&limit=1`, {
      headers: { apikey: SUPABASE_ANON, Authorization: `Bearer ${_accessToken}` },
      signal: AbortSignal.timeout(5000),
    });
    const isOk = r.ok;
    if (isOk !== _isOnline) {
      log(`[useDB] Connectivité changée : ${isOk ? 'ONLINE ✅' : 'OFFLINE ❌'}`);
      notifyOnlineStatus(isOk);
    }
  } catch (err) {
    if (_isOnline) {
      warn('[useDB] Ping Supabase échec → OFFLINE');
      notifyOnlineStatus(false);
    }
  }
}

function startConnectivityMonitoring() {
  if (_pingInterval) return;
  _pingInterval = setInterval(pingSupabase, 10000); // ping toutes les 10s
  
  // Écouter les événements natifs du navigateur
  if (typeof window !== 'undefined') {
    window.addEventListener('online',  () => {
      log('[useDB] Navigateur → ONLINE');
      notifyOnlineStatus(true);
      pingSupabase(); // vérifier immédiatement Supabase
    });
    window.addEventListener('offline', () => {
      warn('[useDB] Navigateur → OFFLINE');
      notifyOnlineStatus(false);
    });
  }
}

function stopConnectivityMonitoring() {
  if (_pingInterval) {
    clearInterval(_pingInterval);
    _pingInterval = null;
  }
}

// ── Token JWT ─────────────────────────────────────────────────────────────────
// Injecté UNIQUEMENT par useAuth via setAuthToken() après validation.
// Jamais lu directement depuis localStorage ici.
let _accessToken  = null;
const _tokenWaiters = [];
let _tokenReady   = false;

// ── Listeners "nouveau token" — notifie tous les useDB montés ────────────────
const _newTokenListeners = new Set();

export function getAuthToken() { return _accessToken; }

export function setAuthToken(token) {
  const prevToken = _accessToken;
  _accessToken = token;

  if (token && !_tokenReady) {
    _tokenReady = true;
    _tokenWaiters.splice(0).forEach(fn => fn());
    // Démarrer le monitoring de connectivité dès qu'on a un token
    startConnectivityMonitoring();
    // Premier ping immédiat
    pingSupabase();
  }

  if (token && token !== prevToken) {
    if (_cache !== null) {
      log('[useDB] Nouveau token → reset cache');
      _cache        = null;
      _cachePromise = null;
      _existingKeys = new Set();
    }
    if (USE_SUPA) {
      getCache().catch(() => {});
      if (!_realtimeWs || _realtimeWs.readyState > 1) connectRealtime();
    }
    // Notifier tous les useDB montés qu'un nouveau token est disponible → re-fetch
    log('[useDB] Nouveau token → notification de', _newTokenListeners.size, 'hooks');
    _newTokenListeners.forEach(fn => fn());
  }

  if (!token) {
    _tokenReady   = false;
    _cache        = null;
    _cachePromise = null;
    _existingKeys = new Set();
    if (_realtimeWs) { _realtimeWs.close(1000); _realtimeWs = null; }
    _realtimeRetries = 0;
    stopConnectivityMonitoring();
  }
}

function waitForToken() {
  if (_tokenReady && _accessToken) return Promise.resolve();
  return new Promise(resolve => {
    let done = false;
    const timer = setTimeout(() => {
      if (done) return;
      done = true;
      warn('[useDB] waitForToken timeout (10s)');
      resolve();
    }, 10_000);
    _tokenWaiters.push(() => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      resolve();
    });
  });
}

export function getAccessToken() { return _accessToken || null; }
export function isOnline() { return _isOnline; }

function writeHeaders() {
  return {
    apikey:         SUPABASE_ANON,
    Authorization:  `Bearer ${_accessToken || SUPABASE_ANON}`,
    'Content-Type': 'application/json',
    Prefer:         'resolution=merge-duplicates,return=minimal',
  };
}

// ─── Écriture : UPSERT ────────────────────────────────────────────────────────
const _pendingWrites = new Map();
const _recentWrites  = new Map();
const WRITE_GRACE_MS = 10_000;
let   _lastAnyWrite  = 0;

function supaSet(key, payload) {
  const prev = _pendingWrites.get(key) ?? Promise.resolve();
  const next = prev.then(() =>
    fetch(`${SUPABASE_URL}/rest/v1/sirh_data?on_conflict=key`, {
      method:  'POST',
      headers: writeHeaders(),
      body:    JSON.stringify({ key, payload }),
    }).then(r => {
      if (!r.ok) return r.text().then(t => { throw new Error(`HTTP ${r.status}: ${t}`); });
      _existingKeys.add(key);
      if (_cache) _cache[key] = payload;
      _recentWrites.set(key, Date.now());
      setTimeout(() => _recentWrites.delete(key), WRITE_GRACE_MS);
      snapshotSave(key, payload); // snapshot local = dernier état Supabase confirmé
      
      // Si l'écriture réussit et qu'on était offline → repasser online
      if (!_isOnline) {
        log('[useDB] Écriture réussie → ONLINE ✅');
        notifyOnlineStatus(true);
      }
      
      log(`[useDB] ✅ UPSERT "${key}"`);
    }).catch(err => {
      // Passer offline UNIQUEMENT sur erreur réseau réelle (pas de réponse).
      // Un 4xx/5xx signifie que Supabase répond → on est toujours online.
      // Un 413 (payload trop gros) ne doit surtout pas déclencher la boucle offline→online.
      const isNetworkError = !err.message.startsWith('HTTP ');
      if (isNetworkError && _isOnline) {
        warn(`[useDB] Écriture échouée (réseau) → OFFLINE : ${err.message}`);
        notifyOnlineStatus(false);
      } else if (!isNetworkError) {
        warn(`[useDB] Écriture échouée (${err.message}) — online maintenu`);
      }
      throw err;
    })
  ).finally(() => {
    if (_pendingWrites.get(key) === next) _pendingWrites.delete(key);
  });
  _lastAnyWrite = Date.now();
  _pendingWrites.set(key, next);
  return next;
}

// ─── Cache global (mémoire uniquement, jamais localStorage) ──────────────────
let _cachePromise = null;
let _cache        = null;
let _existingKeys = new Set();

const _subscribers = new Map();

function subscribe(key, cb) {
  if (!_subscribers.has(key)) _subscribers.set(key, new Set());
  _subscribers.get(key).add(cb);
  return () => _subscribers.get(key)?.delete(cb);
}

function notifyKey(key, payload) {
  if (_pendingWrites.has(key) || _recentWrites.has(key)) return;
  if (_cache) _cache[key] = payload;
  _subscribers.get(key)?.forEach(cb => cb(payload));
}

// ─── Chargement du cache depuis Supabase uniquement ──────────────────────────
function loadCache() {
  if (!_accessToken || _accessToken === SUPABASE_ANON) {
    return Promise.reject(new Error('loadCache: token JWT requis'));
  }
  const savedRecent  = {};
  const savedPending = {};
  _recentWrites.forEach((_, k)  => { if (_cache?.[k] !== undefined) savedRecent[k]  = _cache[k]; });
  _pendingWrites.forEach((_, k) => { if (_cache?.[k] !== undefined) savedPending[k] = _cache[k]; });

  return fetch(`${SUPABASE_URL}/rest/v1/sirh_data?select=key,payload`, {
    headers: { apikey: SUPABASE_ANON, Authorization: `Bearer ${_accessToken}` },
  })
    .then(r => { 
      if (!r.ok) {
        // Supabase répond (code HTTP) → on est online, ce n'est pas une panne réseau.
        throw new Error(`HTTP ${r.status}`);
      }
      // Fetch réussi → mettre à jour _isOnline silencieusement sans notifier les listeners.
      // Notifier ici déclencherait refreshCache() sur tous les hooks → boucle infinie.
      // La détection online/offline est gérée par pingSupabase().
      if (!_isOnline) {
        _isOnline = true;
        log('[useDB] loadCache réussi → ONLINE ✅ (silencieux)');
      }
      return r.json();
    })
    .then(rows => {
      _cache = {};
      _existingKeys = new Set();
      rows.forEach(row => { _cache[row.key] = row.payload; _existingKeys.add(row.key); });
      Object.assign(_cache, savedPending, savedRecent);
      log(`[useDB] Cache chargé — ${rows.length} clés`);
      return _cache;
    })
    .catch(err => {
      // Offline uniquement si Supabase ne répond pas du tout (erreur réseau).
      // Une erreur HTTP (4xx/5xx) = Supabase répond, on est online.
      const isNetworkError = !err.message.startsWith('HTTP ');
      if (isNetworkError && _isOnline) {
        warn(`[useDB] loadCache échec réseau → OFFLINE : ${err.message}`);
        notifyOnlineStatus(false);
      }
      throw err;
    });
}

function getCache() {
  if (_cache) return Promise.resolve(_cache);
  if (_cachePromise) return _cachePromise;
  _cachePromise = loadCache().catch(err => { _cachePromise = null; return Promise.reject(err); });
  return _cachePromise;
}

export function refreshCache() {
  if (!USE_SUPA) return Promise.resolve();
  const pending = [..._pendingWrites.values()];
  return (pending.length > 0 ? Promise.allSettled(pending) : Promise.resolve())
    .then(() => {
      _cachePromise = null;
      return loadCache().then(cache => {
        _subscribers.forEach((cbs, key) => {
          if (_pendingWrites.has(key) || _recentWrites.has(key)) return;
          if (Object.prototype.hasOwnProperty.call(cache, key)) {
            cbs.forEach(cb => cb(cache[key]));
          }
        });
      });
    })
    .catch(err => warn('[useDB] Échec refreshCache :', err.message));
}

// ─── Supabase Realtime WebSocket ──────────────────────────────────────────────
let _realtimeWs      = null;
let _realtimeRetries = 0;
const MAX_REALTIME_RETRIES = 5;

function connectRealtime() {
  if (!USE_SUPA || typeof WebSocket === 'undefined') return;
  if (_realtimeRetries >= MAX_REALTIME_RETRIES) {
    warn(`[Realtime] Abandon après ${MAX_REALTIME_RETRIES} tentatives`);
    return;
  }
  const wsUrl = SUPABASE_URL.replace('https://', 'wss://').replace('http://', 'ws://')
    + '/realtime/v1/websocket?apikey=' + SUPABASE_ANON + '&vsn=1.0.0';
  const ws = new WebSocket(wsUrl);
  _realtimeWs = ws;

  ws.onopen = () => {
    log('[Realtime] Connecté ✅');
    _realtimeRetries = 0;
    
    // WebSocket connecté → marquer online
    if (!_isOnline) {
      log('[Realtime] WebSocket ouvert → ONLINE ✅');
      notifyOnlineStatus(true);
    }
    
    ws.send(JSON.stringify({
      topic: 'realtime:public:sirh_data',
      event: 'phx_join',
      payload: { config: { postgres_changes: [{ event: '*', schema: 'public', table: 'sirh_data' }] } },
      ref: '1',
    }));
  };

  ws.onmessage = (e) => {
    try {
      const msg = JSON.parse(e.data);
      if (msg.event === 'heartbeat' || msg.topic === 'phoenix') {
        ws.send(JSON.stringify({ topic: 'phoenix', event: 'heartbeat', payload: {}, ref: null }));
        return;
      }
      const record = msg.payload?.data?.record ?? msg.payload?.record;
      if (record?.key !== undefined && record?.payload !== undefined) {
        log(`[Realtime] "${record.key}"`);
        notifyKey(record.key, record.payload);
      }
    } catch (err) { warn('[Realtime] Erreur parsing:', err.message); }
  };

  ws.onclose = (e) => {
    log('[Realtime] WebSocket fermé');
    _realtimeWs = null;
    
    // WebSocket fermé anormalement → tenter reconnexion
    if (e.code !== 1000 && _accessToken) {
      _realtimeRetries++;
      const delay = Math.min(5000 * Math.pow(1.5, _realtimeRetries - 1), 30_000);
      log(`[Realtime] Reconnexion dans ${delay}ms (tentative ${_realtimeRetries}/${MAX_REALTIME_RETRIES})`);
      setTimeout(connectRealtime, delay);
    }
  };
  
  ws.onerror = () => {
    warn('[Realtime] Erreur WebSocket');
    // Ne pas marquer offline ici car onclose sera appelé juste après
  };
}

// ─── Refresh au focus fenêtre ─────────────────────────────────────────────────
if (USE_SUPA && typeof window !== 'undefined') {
  let _lastFocusRefresh = 0;
  window.addEventListener('focus', () => {
    if (!_tokenReady || !_accessToken) return;
    const wsState = _realtimeWs?.readyState;
    if (wsState === 0 || wsState === 1) return;
    if (_pendingWrites.size > 0 || _recentWrites.size > 0 || Date.now() - _lastAnyWrite < 15_000) return;
    const now = Date.now();
    if (now - _lastFocusRefresh < 10_000) return;
    _lastFocusRefresh = now;
    log('[useDB] focus → refresh');
    refreshCache();
  });
}

// ─── Hook ─────────────────────────────────────────────────────────────────────
function useDB(collectionKey, init) {
  const [data,    setData]    = useState(init);
  const [loading, setLoading] = useState(true);
  const [synced,  setSynced]  = useState(false);
  const [online,  setOnline]  = useState(_isOnline);
  const mounted    = useRef(true);
  const loadingRef = useRef(loading);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // Garder loadingRef à jour sans recréer le listener
  useEffect(() => { loadingRef.current = loading; }, [loading]);

  // Écouter les changements de connectivité
  useEffect(() => {
    const listener = (status) => {
      if (mounted.current) {
        setOnline(status);
        // Si reconnexion → rafraîchir le cache
        if (status && !loadingRef.current) {
          log(`[useDB] Reconnexion détectée → refresh "${collectionKey}"`);
          refreshCache();
        }
      }
    };
    _onlineListeners.add(listener);
    return () => _onlineListeners.delete(listener);
  }, [collectionKey]); // loadingRef est stable — pas besoin de le lister

  // ── fetchFromSupa : charge/recharge la donnée depuis le cache Supabase ───────
  const fetchFromSupa = useCallback(() => {
    if (!USE_SUPA || !mounted.current) return;

    let resolved = false;
    const safetyTimer = setTimeout(() => {
      if (resolved || !mounted.current) return;
      resolved = true;
      warn(`[useDB] Timeout "${collectionKey}" → snapshot`);
      const snap = snapshotLoad(collectionKey);
      if (mounted.current) { setData(snap !== undefined ? snap : init); setSynced(false); setLoading(false); }
    }, 10_000);

    waitForToken()
      .then(() => getCache())
      .then(cache => {
        clearTimeout(safetyTimer);
        if (resolved || !mounted.current) return;
        resolved = true;
        if (_recentWrites.has(collectionKey) || _pendingWrites.has(collectionKey)) {
          if (mounted.current) { setSynced(true); setLoading(false); }
          return;
        }
        if (Object.prototype.hasOwnProperty.call(cache, collectionKey)) {
          if (mounted.current) setData(cache[collectionKey]);
        } else {
          if (mounted.current) setData(init);
          if (_accessToken && _tokenReady) {
            supaSet(collectionKey, init).catch(err => warn(`[useDB] Init "${collectionKey}" :`, err.message));
          }
        }
        if (mounted.current) { setSynced(true); setLoading(false); }
      })
      .catch(err => {
        clearTimeout(safetyTimer);
        if (resolved || !mounted.current) return;
        resolved = true;
        warn(`[useDB] Supabase KO pour "${collectionKey}" :`, err.message);
        const snap = snapshotLoad(collectionKey);
        if (mounted.current) { setData(snap !== undefined ? snap : init); setSynced(false); setLoading(false); }
      });

    return () => clearTimeout(safetyTimer);
  }, [collectionKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Mode sans Supabase : données sample uniquement
    if (!USE_SUPA) {
      if (mounted.current) { setData(init); setLoading(false); setSynced(false); }
      return;
    }
    fetchFromSupa();
  }, [collectionKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Re-fetch quand un nouveau token arrive (login sans F5) ─────────────────
  useEffect(() => {
    if (!USE_SUPA) return;
    const onNewToken = () => {
      if (!mounted.current) return;
      // Ne pas re-fetcher si une écriture est en attente — risque d'écrasement
      if (_recentWrites.has(collectionKey) || _pendingWrites.has(collectionKey)) {
        log(`[useDB] onNewToken "${collectionKey}" ignoré — écriture en cours`);
        return;
      }
      log(`[useDB] Nouveau token → re-fetch "${collectionKey}"`);
      setSynced(false);
      setLoading(true);
      // NE PAS effacer _cache/_cachePromise ici :
      // setAuthToken l'a déjà fait (une seule fois) avant de notifier les hooks.
      // Si les 4 hooks efface chacun de leur côté, la déduplication de getCache()
      // est contournée et on se retrouve avec 4 loadCache() simultanés.
      // Petit délai pour laisser setAuthToken terminer
      setTimeout(() => { if (mounted.current) fetchFromSupa(); }, 100);
    };
    _newTokenListeners.add(onNewToken);
    return () => _newTokenListeners.delete(onNewToken);
  }, [collectionKey, fetchFromSupa]);

  useEffect(() => {
    if (!USE_SUPA) return;
    return subscribe(collectionKey, payload => {
      if (mounted.current) setData(payload);
    });
  }, [collectionKey]);

  const lastSavedRef = useRef(null);

  const update = useCallback(v => {
    setData(prev => {
      const next = typeof v === 'function' ? v(prev) : v;
      queueMicrotask(() => {
        const serialized = JSON.stringify(next);
        if (serialized === lastSavedRef.current) return;
        lastSavedRef.current = serialized;
        // Écriture uniquement dans Supabase, jamais localStorage
        if (USE_SUPA) {
          supaSet(collectionKey, next).catch(err => {
            warn(`[useDB] Échec sauvegarde "${collectionKey}" :`, err.message);
            // Écrire quand même le snapshot local pour ne pas perdre la modif
            snapshotSave(collectionKey, next);
          });
        }
      });
      return next;
    });
  }, [collectionKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return [data, update, loading, synced, online];
}

export { useDB };
